import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import { useAuth } from '../../context/AuthContext'
import { useOrder } from '../../context/OrderContext'
import { usePartnerAuth } from '../../context/PartnerAuthContext'
import { Coupon } from '../../hooks/useCoupon'
import Stepper from '../../components/Stepper'
import { sendBookingNotificationEmail } from '../../lib/emailService'

/**
 * Page de paiement/confirmation dans le tunnel partenaire.
 *
 * - Affiche le récap avec pricing serveur
 * - Charge automatiquement le premier coupon actif du partenaire
 * - Si aucun coupon actif → réservation sans remise ni commission
 * - En mode partenaire: pickup/delivery = 0 CHF
 * - Commission et réduction calculées côté serveur
 */
const PartnerPaymentPage: React.FC = () => {
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const { draft, computeTotal, resetOrder, goToStep, setPartnerPricing } = useOrder()
  const { partner, partnerProfile } = usePartnerAuth()

  const [loading, setLoading] = useState(false)
  const [pricingLoading, setPricingLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null)
  const [couponLoading, setCouponLoading] = useState(true)
  const [noCouponAvailable, setNoCouponAvailable] = useState(false)
  const [paymentConfirmed, setPaymentConfirmed] = useState(false)

  // Pricing recalculé depuis le serveur
  const [serverPricing, setServerPricing] = useState<any>(null)

  /**
   * Au montage : appeler partner-pricing qui charge automatiquement le coupon PARTNER actif.
   * Plus besoin de charger le coupon séparément : partner-pricing le fait côté serveur.
   */
  useEffect(() => {
    if (!draft.isPartnerMode) {
      navigate('/partner/book')
      return
    }
    if (!draft.service || !draft.option) {
      navigate('/partner/book/options')
      return
    }
    goToStep('PAYMENT')
    void fetchServerPricing()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Appeler l'edge function partner-pricing pour un calcul sécurisé côté serveur.
   * Le coupon PARTNER est automatiquement chargé côté backend.
   */
  const fetchServerPricing = async () => {
    setPricingLoading(true)
    setCouponLoading(true)
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-pricing`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            service_id: draft.service?.id,
            option_id: draft.option?.id,
            partner_id: draft.partnerId,
          }),
        }
      )

      if (!response.ok) {
        const errData = await response.json()
        setError(errData.error || 'Erreur de calcul du prix')
        return
      }

      const pricing = await response.json()
      setServerPricing(pricing)
      setPartnerPricing(pricing)
      setError(null)

      // Déterminer si un coupon a été appliqué par le serveur
      if (pricing.coupon_id) {
        setAppliedCoupon({
          id: pricing.coupon_id,
          code: pricing.coupon_code || '',
          type: 'PARTNER' as const,
          partner_id: pricing.partner_id,
          valid_from: '',
          valid_until: '',
          partner_commission_pct: pricing.partner_commission_pct,
          member_discount_pct: pricing.member_discount_pct,
          user_discount_pct: null,
          max_usage_count: null,
          current_usage_count: 0,
          is_active: true,
        } as any)
        setNoCouponAvailable(false)
      } else {
        setAppliedCoupon(null)
        setNoCouponAvailable(true)
      }
    } catch (err) {
      console.error('Erreur partner-pricing:', err)
      setError('Impossible de calculer le prix. Réessayez.')
    } finally {
      setPricingLoading(false)
      setCouponLoading(false)
    }
  }

  /**
   * Confirmer la commande partenaire
   */
  const handleConfirmOrder = async () => {
    if (!user) {
      alert('Merci de te connecter avant de valider la commande.')
      navigate('/auth/login', { state: { from: { pathname: '/partner/book/payment' } } })
      return
    }

    if (!serverPricing) {
      setError('Pricing non calculé. Réessayez.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // 1. Création de la commande avec les champs partenaire
      const { data: order, error: orderErr } = await supabase
        .from('orders')
        .insert({
          user_id: user.id,
          service_id: draft.service?.id,
          base_price: draft.service?.base_price,
          racket_brand: draft.racketBrand,
          racket_description: draft.racketDescription,
          racket_owner_name: draft.racketOwnerName,
          tension_vertical: draft.tensionVertical,
          tension_horizontal: draft.tensionHorizontal,

          // Transport: adresse partenaire, prix = 0
          pickup_mode_id: null,
          pickup_address: serverPricing.partner_address,
          pickup_distance_km: 0,
          pickup_price: 0,

          delivery_mode_id: null,
          delivery_address: serverPricing.partner_address,
          delivery_distance_km: 0,
          delivery_price: 0,

          // Prix total calculé par le serveur
          total_price: serverPricing.total_price,
          status: 'EN_ATTENTE',
          payment_status: 'OFFLINE_PENDING',

          // Champs partenaire
          partner_id: serverPricing.partner_id,
          partner_account_id: draft.partnerAccountId,
          coupon_id: serverPricing.coupon_id,
          partner_commission_amount: serverPricing.partner_commission_amount,
          member_discount_amount: serverPricing.member_discount_amount,
          original_service_price: serverPricing.original_service_price,
          is_partner_booking: true,
        })
        .select()
        .single()

      if (orderErr) throw orderErr

      const orderId = order.id

      // 2. Insertion de l'option
      if (draft.option) {
        const { error: optErr } = await supabase.from('order_options').insert({
          order_id: orderId,
          option_id: draft.option.id,
          quantity: 1,
        })
        if (optErr) throw optErr
      }

      // 3. Incrémenter le compteur d'utilisation du coupon (si utilisé)
      if (serverPricing.coupon_id) {
        await supabase.rpc('increment_coupon_usage', { coupon_uuid: serverPricing.coupon_id })
          .then(({ error }) => {
            if (error) console.error('Erreur increment coupon usage:', error)
          })
      }

      // 4. Email de notification
      try {
        await sendBookingNotificationEmail({
          orderId,
          orderDetails: {
            created_at: order.created_at,
            total_price: order.total_price,
            status: order.status,
            racket_brand: order.racket_brand,
            racket_description: order.racket_description,
            tension_vertical: order.tension_vertical,
            tension_horizontal: order.tension_horizontal,
            pickup_address: order.pickup_address,
            delivery_address: order.delivery_address,
            pickup_distance_km: 0,
            delivery_distance_km: 0,
            pickup_price: 0,
            delivery_price: 0,
            service_name: draft.service?.name,
            service_base_price: draft.service?.base_price,
            pickup_mode_label: 'Partenaire (sur place)',
            delivery_mode_label: 'Partenaire (sur place)',
            options: draft.option
              ? [{ label: draft.option.label, price_delta: draft.option.price_delta }]
              : [],
          },
          customerDetails: {
            full_name: profile?.full_name,
            email: user.email,
            phone: profile?.phone,
            address_line1: serverPricing.partner_address,
            city: '',
          },
        })
      } catch (emailError) {
        console.error("Erreur email notification:", emailError)
      }

      // 5. Succès
      setSuccess(true)
      resetOrder()
      navigate(`/order/confirmation?orderId=${orderId}`)
    } catch (e: any) {
      console.error('Erreur création commande partenaire:', e)
      setError('Impossible de valider la commande. Réessayez plus tard.')
    } finally {
      setLoading(false)
    }
  }

  const total = serverPricing?.total_price ?? computeTotal()

  return (
    <div className="card">
      <Stepper />

      {/* Bannière partenaire */}
      {partner && (
        <div
          data-testid="partner-banner"
          style={{
            background: `${partner.color_code}22`,
            border: `1px solid ${partner.color_code}`,
            borderRadius: '0.5rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
          }}
        >
          <span style={{ fontWeight: 600, color: partner.color_code }}>
            🏢 Réservation {partner.name}
          </span>
        </div>
      )}

      <div className="card-header">
        <h1 className="card-title">Confirmation de la commande</h1>
      </div>

      {/* Récapitulatif */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>Récapitulatif</h2>
        <ul style={{ fontSize: '.9rem', color: '#e2e8f0' }}>
          <li>
            <strong>Service :</strong> {draft.service?.name}{' '}
            ({draft.service?.base_price?.toFixed(2)} CHF)
          </li>

          {draft.option && (
            <li>
              <strong>Option :</strong> {draft.option.label}{' '}
              ({draft.option.price_delta.toFixed(2)} CHF)
            </li>
          )}

          {draft.racketOwnerName && (
            <li>
              <strong>Propriétaire :</strong> {draft.racketOwnerName}
            </li>
          )}

          <li>
            <strong>Raquette :</strong> {draft.racketBrand}
            {draft.racketDescription && ` — ${draft.racketDescription}`}
          </li>

          <li>
            <strong>Tension :</strong> {draft.tensionVertical} / {draft.tensionHorizontal} kg
          </li>

          {/* Transport: toujours 0 en mode partenaire */}
          <li>
            <strong>Prise en charge :</strong> Partenaire (sur place) — 0.00 CHF
          </li>
          <li>
            <strong>Livraison :</strong> Partenaire (sur place) — 0.00 CHF
          </li>
        </ul>
      </div>

      {/* Coupon partenaire (auto-détecté) */}
      {couponLoading && (
        <p style={{ color: '#9ca3af', marginBottom: '1rem' }}>Recherche du coupon partenaire…</p>
      )}

      {/* Aucun coupon actif */}
      {!couponLoading && noCouponAvailable && (
        <div
          data-testid="no-coupon-info"
          style={{
            background: 'rgba(148, 163, 184, 0.08)',
            border: '1px solid rgba(148, 163, 184, 0.3)',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1.5rem',
            fontSize: '.9rem',
            color: '#9ca3af',
          }}
        >
          Aucun avantage partenaire actif — réservation sans remise ni commission.
        </div>
      )}

      {/* Coupon auto-appliqué */}
      {!couponLoading && appliedCoupon && (
        <div
          data-testid="coupon-applied"
          style={{
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>🎟️</span>
          <div>
            <strong style={{ color: '#22c55e' }}>{appliedCoupon.code}</strong>
            {appliedCoupon.partner && (
              <span style={{ color: '#9ca3af', marginLeft: '0.5rem', fontSize: '.85rem' }}>
                ({(appliedCoupon.partner as any).name})
              </span>
            )}
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '.85rem', color: '#e2e8f0' }}>
              Réduction: -{appliedCoupon.member_discount_pct}%
              {' · '}Commission: {appliedCoupon.partner_commission_pct}%
            </p>
          </div>
        </div>
      )}

      {/* Affichage des réductions et commissions si coupon appliqué */}
      {serverPricing && serverPricing.coupon_id && (
        <div
          data-testid="partner-coupon-summary"
          style={{
            background: 'rgba(34, 197, 94, 0.08)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '0.5rem',
            padding: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <div style={{ fontWeight: 600, color: '#22c55e', marginBottom: '0.5rem' }}>
            🎟️ Coupon partenaire appliqué
          </div>
          <ul style={{ fontSize: '.9rem', margin: 0, paddingLeft: '1rem' }}>
            <li>
              Réduction membre :{' '}
              <strong>-{serverPricing.member_discount_pct}%</strong>{' '}
              ({serverPricing.member_discount_amount.toFixed(2)} CHF)
            </li>
            <li>
              Commission partenaire :{' '}
              <strong>{serverPricing.partner_commission_pct}%</strong>{' '}
              ({serverPricing.partner_commission_amount.toFixed(2)} CHF)
            </li>
            <li style={{ color: '#9ca3af', fontSize: '.85rem', marginTop: '0.25rem' }}>
              Commission et réduction appliquées uniquement sur le service de base
              (hors options)
            </li>
          </ul>
        </div>
      )}

      {pricingLoading && (
        <p style={{ color: '#9ca3af' }}>Calcul du prix en cours…</p>
      )}

      {/* TOTAL */}
      <div
        data-testid="payment-total"
        style={{
          borderTop: '1px solid rgba(148,163,184,0.3)',
          paddingTop: '1rem',
          fontSize: '1.1rem',
          fontWeight: 700,
          color: '#22c55e',
        }}
      >
        Total à payer : {total.toFixed(2)} CHF
      </div>

      {/* Détail serveur: prix original vs final si réduction */}
      {serverPricing && serverPricing.member_discount_amount > 0 && (
        <div style={{ fontSize: '.85rem', color: '#9ca3af', marginTop: '0.25rem' }}>
          <s>{(serverPricing.original_service_price + serverPricing.option_price).toFixed(2)} CHF</s>
          {' → '}{total.toFixed(2)} CHF
          {' '}(économie de {serverPricing.member_discount_amount.toFixed(2)} CHF)
        </div>
      )}

      {/* Paiement */}
      <div style={{ marginTop: '1.5rem' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            fontSize: '.95rem',
            color: paymentConfirmed ? '#22c55e' : '#e2e8f0',
          }}
        >
          <input
            type="checkbox"
            checked={paymentConfirmed}
            onChange={(e) => setPaymentConfirmed(e.target.checked)}
            data-testid="payment-confirmed-checkbox"
            style={{ width: 18, height: 18, accentColor: '#22c55e', cursor: 'pointer' }}
          />
          Paiement effectué
        </label>

        {!paymentConfirmed && (
          <p
            style={{
              color: '#f59e0b',
              fontSize: '.85rem',
              marginTop: '0.5rem',
              background: 'rgba(245,158,11,0.1)',
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid rgba(245,158,11,0.3)',
            }}
            data-testid="payment-reminder"
          >
            ⚠️ Merci d'encaisser le client puis de cocher « Paiement effectué » pour pouvoir confirmer la commande.
          </p>
        )}

        <button
          data-testid="confirm-order-button"
          className="btn btn-primary"
          onClick={handleConfirmOrder}
          disabled={loading || pricingLoading || !paymentConfirmed}
          style={{ marginTop: '1rem' }}
        >
          {loading ? 'Validation…' : 'Confirmer ma commande'}
        </button>
      </div>

      {error && (
        <p style={{ color: '#f87171', marginTop: '1rem' }}>{error}</p>
      )}

      {success && (
        <p style={{ color: '#22c55e', marginTop: '1rem' }}>
          ✔ Commande enregistrée !
        </p>
      )}
    </div>
  )
}

export default PartnerPaymentPage
